import { makeAutoObservable } from 'mobx'
import { createContext, FC, useContext } from 'react'
import { LeftSidebarStore } from './LeftSidebarStore'
import { CommentsStore } from './CommentsStore'
import { TodosStore } from './TodosStore'
import type { DocStore } from 'stores/DocStore'
import type { QueryClient } from 'react-query'
import defaultQueryClient from '../queryClient'
import { UsersStore } from './UsersStore'
import { NotificationsStore } from './NotificationsStore'
import { ChannelsStore } from './ChannelsStore'
import { WorkspacePortalStore } from './WorkspacePortalStore'
import { AlmanacFilesStore } from './AlmanacFilesStore'
import { LayoutStore } from './LayoutStore'
import { HandbooksStore } from './HandbooksStore'
import { FilesViewSchemaStore } from './FilesViewSchemaStore'
import { FeatureFlagsStore } from 'stores/FeatureFlagsStore'
import { Theme } from './Theme'
import { WorkspaceTemplatesStore } from './WorkspaceTemplatesStore'
import { OnboardingStore } from './OnboardingStore'
import { ConfettiStore } from './ConfettiStore'
import { RightRailPanelStore } from './RightRailPanelStore'
import { SEOAnalysisStore } from './SEOAnalysisStore'
import { TimerStore } from './TimerStore'
import { AiGenerationStore } from './AiGeneration'
import { DesignStore } from './DesignStore'
import { VisualElementsStore } from './VisualElementsStore'
import { GoogleFontsStore } from './GoogleFonts/GoogleFontsStore'
import { CustomFontsStore } from './CustomFonts/CustomFontsStore'
import { DrawersStore } from './DrawersStore'
import { TierUsageStore } from './TierUsageStore'
import { PaywallStore } from './PaywallStore'
import { BlazeFontsStore } from './BlazeFonts/BlazeFontsStore'
import { FontsStore } from './FontsStore'
import { CreditUsageStore } from './CreditUsageStore'
import { GenerationTimestampStore } from './GenerationTimestampStore'
import { ContentGenerationStore } from './ContentGenerationStore'

export const defaultMobxCacheMaxAge = 86400000 * 3 // 3 days,

export class RootStore {
  queryClient: QueryClient
  comments: CommentsStore
  todos: TodosStore
  docStore?: DocStore
  designStore?: DesignStore
  users: UsersStore
  notifications: NotificationsStore
  channels: ChannelsStore
  workspacePortal: WorkspacePortalStore
  leftSidebarStore: LeftSidebarStore
  files: AlmanacFilesStore
  layout: LayoutStore
  handbooks: HandbooksStore
  filesViewSchemas: FilesViewSchemaStore
  featureFlags: FeatureFlagsStore
  workspaceTheme: Theme
  workspaceTemplatesStore: WorkspaceTemplatesStore
  onboardingStore: OnboardingStore
  confettiStore: ConfettiStore
  rightRailPanelStore: RightRailPanelStore
  seoAnalysisStore: SEOAnalysisStore
  timerStore: TimerStore
  aiGeneration: AiGenerationStore
  visualElements: VisualElementsStore
  googleFonts: GoogleFontsStore
  customFonts: CustomFontsStore
  blazeFonts: BlazeFontsStore
  drawersStore: DrawersStore
  tierUsageStore: TierUsageStore
  creditUsageStore: CreditUsageStore
  paywallStore: PaywallStore
  fonts: FontsStore
  generationTimestampStore: GenerationTimestampStore
  contentGeneration: ContentGenerationStore

  constructor(queryClient: QueryClient = defaultQueryClient, shouldPersist = false) {
    makeAutoObservable(this)
    this.queryClient = queryClient
    this.comments = new CommentsStore(this, shouldPersist)
    this.todos = new TodosStore(this, shouldPersist)
    this.users = new UsersStore(this, shouldPersist)
    this.notifications = new NotificationsStore(this, shouldPersist)
    this.channels = new ChannelsStore(this)
    this.workspacePortal = new WorkspacePortalStore(this)
    this.leftSidebarStore = new LeftSidebarStore(this, shouldPersist)
    this.files = new AlmanacFilesStore(this)
    this.layout = new LayoutStore(this)
    this.handbooks = new HandbooksStore(this, shouldPersist)
    this.filesViewSchemas = new FilesViewSchemaStore(this, shouldPersist)
    this.featureFlags = new FeatureFlagsStore()
    this.workspaceTheme = new Theme()
    this.workspaceTemplatesStore = new WorkspaceTemplatesStore(this)
    this.onboardingStore = new OnboardingStore(this)
    this.confettiStore = new ConfettiStore(this)
    this.rightRailPanelStore = new RightRailPanelStore(this)
    this.seoAnalysisStore = new SEOAnalysisStore(this)
    this.timerStore = new TimerStore(this)
    this.aiGeneration = new AiGenerationStore(this)
    this.visualElements = new VisualElementsStore(this)
    this.googleFonts = new GoogleFontsStore(this)
    this.customFonts = new CustomFontsStore(this)
    this.blazeFonts = new BlazeFontsStore(this)
    this.drawersStore = new DrawersStore(this)
    this.tierUsageStore = new TierUsageStore(this)
    this.creditUsageStore = new CreditUsageStore(this)
    this.paywallStore = new PaywallStore(this)
    this.fonts = new FontsStore(this)
    this.generationTimestampStore = new GenerationTimestampStore(this)
    this.contentGeneration = new ContentGenerationStore()
  }
}

export const rootStore = new RootStore(defaultQueryClient)

export const RootStoreContext = createContext<RootStore>(rootStore)
export const useRootStore = () => {
  return useContext(RootStoreContext)
}

export const RootStoreProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <RootStoreContext.Provider value={rootStore}>{children}</RootStoreContext.Provider>
}
